// src/components/DashboardLayout.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { theme } from "../utils/theme";

const C = theme.colors;

const DashboardLayout = ({ children }) => {
  const { user, userData, profile, role, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAccountsSub, setShowAccountsSub] = useState(false);

  // Helper to format role/designation for display
  const displayRole = (role === "student" || role === "staff_limited") 
    ? (profile?.designation || (role === "student" ? "Student" : "Staff")) 
    : role;

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const getNavItems = () => {
    const mainItems = [
      { id: "overview",    label: "Overview",    icon: "▦", path: "/admin" },
      { id: "people",      label: "People",      icon: "👥", path: "/admin/people" },
      { id: "courses",     label: "Courses",     icon: "📚", path: "/admin/courses" },
      { id: "reports",     label: "Attendance Reg.", icon: "▣", path: "/reports" },
      { id: "enroll",      label: "Enroll New",  icon: "✦", path: "/enroll" },
      { id: "progress",    label: "Progress Stats", icon: "◐", path: "/admin/progress" },
      { id: "self-progress", label: "Self Log", icon: "📝", path: "/admin/self-progress" },
      { id: "log-report",  label: "Log Timeline", icon: "📊", path: "/log-report" },
      { id: "diet-report", label: "Diet Timeline", icon: "🥗", path: "/diet-report" },
      { id: "typing",      label: "Typing Tutor",  icon: "⌨", path: "/typing-tutor" },
      { id: "skills",      label: "Skills",        icon: "🎯", path: "/admin/skills" },
      { id: "crm",         label: "CRM",           icon: "📞", path: "/admin/crm" },
      { id: "whatsapp",    label: "Send Message", icon: "💬", path: "/admin/whatsapp" },
      { id: "messages",    label: "Message Log", icon: "📬", path: "/admin/messages" },
      { id: "ai-chat",     label: "Life AI Assistant", icon: "✨", path: "/admin/ai-chat" },
      { id: "typing-settings", label: "Typing Settings", icon: "⚙️", path: "/admin/typing-settings" },
      ];

      const accountingItems = [
      { id: "customers",   label: "Customers",   icon: "🏢", path: "/admin/customers" },
      { id: "accounts",    label: "Accounts",    icon: "📒", path: "/admin/accounts" },
      { id: "invoices",    label: "Invoices",    icon: "📄", path: "/admin/invoices" },
      { id: "items",       label: "Item Master", icon: "📦", path: "/admin/items" },
      { id: "expenses",    label: "Expenses",    icon: "💸", path: "/admin/expenses" },
      { id: "jv",          label: "Journal Voucher", icon: "📓", path: "/admin/jv" },
      { id: "reports",     label: "Fin. Reports", icon: "📊", path: "/admin/financial-reports" },
      ];

      const finalItems = [
      { id: "certificates", label: "Certificates", icon: "🎓", path: "/admin/certificates" },
      { id: "kiosk",        label: "Attendance Kiosk", icon: "📸", path: "/admin/kiosk" },
      ];

    if (role === "student" || role === "staff_limited") {
      return {
        main: [
          { id: "portal-overview", label: "My Dashboard", icon: "🏠", path: "/student" },
          { id: "portal-courses",  label: "My Courses",   icon: "📚", path: "/student/courses" },
          { id: "portal-self-log", label: "Self Log",     icon: "📝", path: "/student/self-log" },
          { id: "portal-log-report", label: "Log Timeline", icon: "📊", path: "/student/log-report" },
          { id: "portal-diet-report", label: "Diet Timeline", icon: "🍱", path: "/student/diet-report" },
          { id: "portal-skills",    label: "My Skills",     icon: "🎯", path: "/student/skills" },
          { id: "portal-crm",       label: "My CRM",        icon: "📞", path: "/student/crm" },
          { id: "portal-typing",    label: "Typing Tutor",  icon: "⌨", path: "/typing-tutor" },
          { id: "portal-ai",       label: "Life AI Assistant", icon: "✨", path: "/student/ai-assistant" },
          { id: "portal-diary",    label: "My Diary",     icon: "📔", path: "/student/diary" },
          { id: "portal-password", label: "Change Password", icon: "🔑", path: "/student/password" },
          { id: "portal-profile",  label: "My Profile",      icon: "👤", path: "/student/profile" },
        ],
        accounting: [],
        final: []
      };
    }

    if (role === "instructor") {
      return {
        main: [
          { id: "overview",  label: "Overview",  icon: "▦", path: "/admin" },
          { id: "people",    label: "People",    icon: "👥", path: "/admin/people" },
          { id: "courses",   label: "Courses",   icon: "📚", path: "/admin/courses" },
          { id: "reports",   label: "Attendance Reg.", icon: "▣", path: "/reports" },
          { id: "enroll",    label: "Enroll New", icon: "✦", path: "/enroll" },
          { id: "progress",  label: "Progress Stats", icon: "◐", path: "/admin/progress" },
          { id: "log-report", label: "Log Timeline", icon: "📊", path: "/log-report" },
          { id: "diet-report", label: "Diet Timeline", icon: "🥗", path: "/diet-report" },
          { id: "typing",    label: "Typing Tutor", icon: "⌨", path: "/typing-tutor" },
          { id: "skills",    label: "Skills", icon: "🎯", path: "/admin/skills" },
          { id: "messages",  label: "Message Log", icon: "📬", path: "/admin/messages" },
          { id: "ai-chat",   label: "Life AI Assistant", icon: "✨", path: "/admin/ai-chat" },
          { id: "typing-settings", label: "Typing Settings", icon: "⚙️", path: "/admin/typing-settings" },
          { id: "kiosk",     label: "Attendance Kiosk", icon: "📸", path: "/admin/kiosk" },
        ],
        accounting: [],
        final: []
      };
    }

    return { main: mainItems, accounting: accountingItems, final: finalItems };
  };

  const navGroups = getNavItems();
  const allAccountingPaths = ["/admin/customers", "/admin/accounts", "/admin/invoices", "/admin/items", "/admin/expenses", "/admin/jv", "/admin/financial-reports"];
  const isAccountingActive = allAccountingPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (isAccountingActive) setShowAccountsSub(true);
  }, [isAccountingActive]);

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  const NavItem = ({ item, isSub = false }) => (
    <Link
      to={item.path}
      onClick={() => setIsMobileMenuOpen(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: isSub ? "8px 12px" : "9px 12px",
        borderRadius: 8,
        marginBottom: 2,
        cursor: "pointer",
        textDecoration: "none",
        background: isActive(item.path) ? C.accentSoft : "transparent",
        color: isActive(item.path) ? C.accent : C.muted,
        fontSize: isSub ? 13 : 14,
        fontWeight: isActive(item.path) ? 600 : 400,
        transition: ".2s"
      }}
    >
      <span style={{ fontSize: isSub ? 11 : 12 }}>{item.icon}</span>
      {item.label}
    </Link>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: theme.fonts.sans }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        .mobile-only { display: none !important; }
        .desktop-only { display: block !important; }

        @media (max-width: 1024px) {
          .mobile-only { display: flex !important; }
          .desktop-only { display: none !important; }
          .main-content { margin-left: 0 !important; padding: 16px !important; }
        }

        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1000;
          backdrop-filter: blur(2px);
        }

        .mobile-menu-content {
          width: 280px;
          height: 100%;
          background: ${C.surface};
          animation: slideIn 0.3s ease-out;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .hamburger {
          width: 30px;
          height: 30px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .hamburger div {
          width: 24px;
          height: 2px;
          background: ${C.text};
          border-radius: 10px;
          transition: 0.3s;
        }
      `}</style>

      {/* Desktop Sidebar */}
      <aside className="desktop-only" style={{ width: 220, background: C.surface, borderRight: `1px solid ${C.border}`, position: "fixed", height: "100vh", zIndex: 10 }}>
        <SidebarContent 
          navGroups={navGroups} 
          role={role} 
          user={user} 
          userData={userData}
          profile={profile}
          logout={logout} 
          showAccountsSub={showAccountsSub} 
          setShowAccountsSub={setShowAccountsSub} 
          isAccountingActive={isAccountingActive}
          NavItem={NavItem}
          displayRole={displayRole}
        />
      </aside>

      {/* Mobile Top Header */}
      <header className="mobile-only" style={{ height: 60, width: "100%", background: C.surface, borderBottom: `1px solid ${C.border}`, alignItems: "center", justifyContent: "space-between", padding: "0 20px", position: "fixed", top: 0, zIndex: 100 }}>
        <button className={`hamburger ${isMobileMenuOpen ? "open" : ""}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <div /><div /><div />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{userData?.name || "Pankaj Computers"}</div>
            <div style={{ fontSize: 10, color: C.muted }}>{displayRole?.toUpperCase()}</div>
          </div>
          {profile?.photoURL && (
            <img src={profile.photoURL} alt="Profile" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}` }} />
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-menu-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 18px" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Menu</div>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: C.muted }}>✕</button>
            </div>
            <SidebarContent 
              navGroups={navGroups} 
              role={role} 
              user={user} 
              userData={userData}
              profile={profile}
              logout={logout} 
              showAccountsSub={showAccountsSub} 
              setShowAccountsSub={setShowAccountsSub} 
              isAccountingActive={isAccountingActive}
              NavItem={NavItem}
              displayRole={displayRole}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content" style={{ marginLeft: 220, flex: 1, padding: 28, minWidth: 0 }}>
        <div className="mobile-only" style={{ height: 60 }} /> {/* Spacer for fixed mobile header */}
        {children}
      </main>
    </div>
  );
};

const SidebarContent = ({ navGroups, role, user, userData, profile, logout, showAccountsSub, setShowAccountsSub, isAccountingActive, NavItem, displayRole }) => (
  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
    <nav style={{ flex: 1, padding: "4px 10px", overflowY: "auto" }}>
      {/* Main Items */}
      {navGroups.main.map(n => <NavItem key={n.id} item={n} />)}

    {/* Accounts Toggle (Admin Only) */}
    {(role === "admin" || role === "trainer") && (
      <>
        <div 
          onClick={() => setShowAccountsSub(!showAccountsSub)} 
          style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between", 
            padding: "9px 12px", 
            borderRadius: 8, 
            marginBottom: 2, 
            cursor: "pointer", 
            background: isAccountingActive ? C.accentSoft : "transparent", 
            color: isAccountingActive ? C.accent : C.muted, 
            fontSize: 14, 
            fontWeight: isAccountingActive ? 600 : 500 
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12 }}>💰</span>
            <span>Accounts +</span>
          </div>
          <span style={{ fontSize: 10, transition: ".3s", transform: showAccountsSub ? "rotate(90deg)" : "none" }}>▶</span>
        </div>

        {showAccountsSub && (
          <div style={{ marginLeft: 14, borderLeft: `1px solid ${C.border}`, paddingLeft: 4, marginBottom: 8 }}>
            {navGroups.accounting.map(n => <NavItem key={n.id} item={n} isSub={true} />)}
          </div>
        )}
      </>
    )}

    {/* Final Items */}
    {navGroups.final.map(n => <NavItem key={n.id} item={n} />)}

    <div style={{ height: 1, background: C.border, margin: "10px 0" }} />
    
    {(role === "admin" || role === "trainer") && (
      <>
        <Link to="/admin/kiosks" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8, marginBottom: 2, cursor: "pointer", textDecoration: "none", color: C.muted, fontSize: 14 }}>
          <span style={{ fontSize: 12 }}>🖥</span>Kiosks
        </Link>
        <Link to="/admin/settings" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8, marginBottom: 2, cursor: "pointer", textDecoration: "none", color: C.muted, fontSize: 14 }}>
          <span style={{ fontSize: 12 }}>⚙</span>Settings
        </Link>
      </>
    )}
    </nav>

    <div style={{ padding: "14px 18px", borderTop: `1px solid ${C.border}`, background: C.surface }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        {profile?.photoURL ? (
          <img src={profile.photoURL} alt="User" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}` }} />
        ) : (
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.accentSoft, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
            {userData?.name?.charAt(0)}
          </div>
        )}
        <div style={{ overflow: "hidden" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{userData?.name}</div>
          <div style={{ fontSize: 11, color: C.accent, fontWeight: 600, textTransform: "capitalize" }}>{displayRole}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", marginBottom: 10 }}>{user?.email}</div>
      <button onClick={logout} style={{ fontSize: 12, color: C.red, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>Sign out</button>
    </div>
  </div>
);

export default DashboardLayout;
